import {
  DEFAULT_PURPOSE_TAGS,
  haversineMeters,
  summarizeRatings,
  walkingMinutes,
} from '@mapshare/core';

/**
 * @fileoverview The landing page's demo: a fictional organization and its
 * fictional places (2026-09-25, OQ-48).
 *
 * **Everything here is made up, and nothing here comes from anywhere.** The
 * organization, the shop names, the addresses, the members, the descriptions,
 * ratings and visits are invented for the page; no row of any environment's D1 and no response of
 * any place provider is behind them (invariant 3). The demo never calls the
 * API for them — it filters this array in the browser — so it costs nothing
 * and cannot show a real organization's data by mistake.
 *
 * The coordinates are real points on the map (the basemap is the real one,
 * drawn with its attribution through `MapView`), around Kanda in Tokyo, but
 * the shops at them are not: the page says so next to the demo and on every
 * card. The addresses are deliberately fictional (「架空区」) so that no one
 * mistakes a card for a listing.
 */

export interface LatLng {
  readonly lat: number;
  readonly lng: number;
}

export interface DemoVisit {
  readonly author: string;
  /** The day they went, `YYYY-MM-DD`. */
  readonly day: string;
  /** Their impression; may be empty, as a real one may. */
  readonly body: string;
}

export interface DemoPlace extends LatLng {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  /** Labels of `DEFAULT_PURPOSE_TAGS` — the tags every organization starts with. */
  readonly tags: readonly string[];
  /** The place's shared description (OQ-52): what the shop is like, one for everyone. */
  readonly description: string;
  /** Each member's rating, one per member (OQ-52 (ii)). */
  readonly ratings: readonly number[];
  /** Members' visits, latest first — as the real card reads them. */
  readonly visits: readonly DemoVisit[];
  /** When it was registered, `YYYY-MM-DD`: 「新しく登録した順」. */
  readonly registeredOn: string;
  readonly addedBy: string;
}

export const DEMO_ORG = {
  name: 'さくら商事',
  /** The organization's address marker (`anchor`, like the real map's). */
  anchorLabel: '本社',
  anchor: { lat: 35.69195, lng: 139.7703 },
} as const;

export const DEMO_PLACES: readonly DemoPlace[] = [
  {
    id: 'demo-yamabuki',
    name: 'そば処 やまぶき',
    address: '架空区さくら町1-2',
    lat: 35.69261,
    lng: 139.76947,
    tags: ['ランチ', '一人でも'],
    description:
      '12時前なら並ばずに入れる。大盛りは無料。カウンターが中心で、テーブル席は4人まで。',
    ratings: [4, 5, 4],
    visits: [
      { author: '佐藤', day: '2026-09-26', body: '天せいろ。11時50分で待ちなし。' },
      { author: '田中', day: '2026-09-19', body: '冷やしたぬきが夏の定番。' },
      { author: '伊藤', day: '2026-09-12', body: '' },
      { author: '佐藤', day: '2026-09-05', body: 'かけそばで軽く。' },
    ],
    registeredOn: '2026-08-20',
    addedBy: '佐藤',
  },
  {
    id: 'demo-hidamari',
    name: '喫茶 ひだまり',
    address: '架空区さくら町2-8',
    lat: 35.6914,
    lng: 139.77129,
    tags: ['打合せ・カフェ'],
    description: '奥の4人席は電源あり。打合せに使いやすい。15時以降は空いている。',
    ratings: [4, 4],
    visits: [
      { author: '高橋', day: '2026-09-25', body: '15時から1時間の打合せで。静かだった。' },
      { author: '鈴木', day: '2026-09-17', body: '' },
    ],
    registeredOn: '2026-08-22',
    addedBy: '鈴木',
  },
  {
    id: 'demo-tsukimi',
    name: '和菓子 つきみ堂',
    address: '架空区もみじ通り3-1',
    lat: 35.69349,
    lng: 139.77096,
    tags: ['差し入れ・手土産', 'スイーツ'],
    description: 'どら焼きの10個入りは前日までに電話で予約。日持ちは3日。',
    ratings: [5, 5, 4, 5],
    visits: [
      { author: '高橋', day: '2026-09-24', body: '来客の手土産に。「どこのですか」と聞かれた。' },
      { author: '渡辺', day: '2026-09-10', body: '栗どら焼きは秋だけ。' },
      { author: '田中', day: '2026-08-28', body: '' },
    ],
    registeredOn: '2026-08-21',
    addedBy: '高橋',
  },
  {
    id: 'demo-kagaribi',
    name: '酒場 かがり火',
    address: '架空区さくら町4-5',
    lat: 35.69074,
    lng: 139.76986,
    tags: ['飲み会', 'ディナー'],
    description: '個室は8名まで。金曜は早めに予約を。飲み放題は2時間から。',
    ratings: [4, 3],
    visits: [
      { author: '田中', day: '2026-09-20', body: '歓迎会で個室を使った。8人でちょうど。' },
      { author: '鈴木', day: '2026-09-06', body: '' },
    ],
    registeredOn: '2026-08-25',
    addedBy: '田中',
  },
  {
    id: 'demo-komachi',
    name: '定食 こまち',
    address: '架空区いちょう坂5-3',
    lat: 35.69223,
    lng: 139.77195,
    tags: ['ランチ'],
    description: '日替わりは850円。ご飯のおかわり自由。',
    ratings: [4, 4, 3, 4],
    visits: [
      { author: '伊藤', day: '2026-09-29', body: '日替わりは生姜焼き。' },
      { author: '渡辺', day: '2026-09-26', body: '' },
      { author: '伊藤', day: '2026-09-22', body: '' },
      { author: '佐藤', day: '2026-09-18', body: '12時半を過ぎると空いてくる。' },
      { author: '田中', day: '2026-09-11', body: '' },
      { author: '伊藤', day: '2026-09-08', body: '' },
    ],
    registeredOn: '2026-08-20',
    addedBy: '伊藤',
  },
  {
    id: 'demo-komugi',
    name: 'パン工房 こむぎ',
    address: '架空区もみじ通り1-9',
    lat: 35.6903,
    lng: 139.77167,
    tags: ['テイクアウト', '差し入れ・手土産'],
    description: '朝8時から。会議の差し入れにちょうどいい。12個以上は前日に電話で。',
    ratings: [4],
    visits: [{ author: '渡辺', day: '2026-09-16', body: '朝の勉強会に12個。' }],
    registeredOn: '2026-09-03',
    addedBy: '渡辺',
  },
  {
    id: 'demo-komichi',
    name: 'ビストロ 小径',
    address: '架空区いちょう坂2-4',
    lat: 35.69387,
    lng: 139.76892,
    tags: ['ディナー', '接待'],
    description: '接待はカウンターより奥のテーブル席で。個室は6名まで。コースは要予約。',
    ratings: [5, 4],
    visits: [{ author: '佐藤', day: '2026-09-23', body: '取引先と4人で。個室が落ち着いていた。' }],
    registeredOn: '2026-09-15',
    addedBy: '佐藤',
  },
  {
    id: 'demo-yokaze',
    name: 'ラーメン 夜風',
    address: '架空区さくら町6-1',
    lat: 35.69112,
    lng: 139.76854,
    tags: ['深夜', '一人でも', 'ランチ'],
    // Just registered: nobody has rated it or recorded a visit yet.
    description: '深夜2時まで。残業の後に。',
    ratings: [],
    visits: [],
    registeredOn: '2026-09-28',
    addedBy: '田中',
  },
];

/**
 * A demo place in the shape the real list sorts and summarizes
 * (`explore/sort.ts`): the average as the API rounds it, how many rated,
 * the visits and when it was registered.
 */
export function demoActivity(place: DemoPlace): {
  rating: { average: number | null; count: number };
  visitCount: number;
  createdAt: number;
} {
  return {
    rating: summarizeRatings(place.ratings),
    visitCount: place.visits.length,
    createdAt: Date.parse(`${place.registeredOn}T00:00:00+09:00`),
  };
}

/** Who went most recently, latest first, each once — the card's 「最近行った人」. */
export function demoRecentVisitors(place: DemoPlace, limit = 3): string[] {
  return [...new Set(place.visits.map((visit) => visit.author))].slice(0, limit);
}

/**
 * The tags the demo offers as chips: the default purpose tags that at least
 * one demo place carries, in the default order.
 */
export const DEMO_TAGS: readonly string[] = DEFAULT_PURPOSE_TAGS.map((tag) => tag.label).filter(
  (label) => DEMO_PLACES.some((place) => place.tags.includes(label)),
);

export interface DemoFilter {
  readonly query: string;
  readonly tag: string | null;
}

/** The places a search and a tag leave, in their original order. */
export function filterDemoPlaces(
  places: readonly DemoPlace[],
  filter: DemoFilter,
): readonly DemoPlace[] {
  const query = filter.query.trim().normalize('NFKC').toLowerCase();
  return places.filter((place) => {
    if (filter.tag !== null && !place.tags.includes(filter.tag)) return false;
    if (query.length === 0) return true;
    // Name, address and description, as the organization's search reads them (OQ-57 (4)).
    return `${place.name} ${place.address} ${place.description}`
      .normalize('NFKC')
      .toLowerCase()
      .includes(query);
  });
}

/**
 * How far from the organization's address, the way the real map says it: a
 * straight line, labelled as one (docs/03-domain-model.md 3.4).
 */
export function demoDistance(place: LatLng): { meters: number; minutes: number } {
  const meters = haversineMeters(DEMO_ORG.anchor, place);
  return { meters, minutes: Math.max(1, Math.round(walkingMinutes(meters))) };
}

/** The demo map's zoom: every place fits in the phone's visible band. */
export const DEMO_ZOOM = 15;

/**
 * How far above the map's centre the visible band's middle is, in pixels: the
 * search and tags cover the top of the phone and the sheet its lower 44%, so a
 * point placed at the map's centre would sit behind the sheet.
 */
export const DEMO_BAND_OFFSET_PX = 90;

/**
 * The map centre that puts `target` in the middle of the visible band: the
 * same point, moved south by `offsetPx` at `zoom` (MapLibre's 512px tiles).
 */
export function demoCameraCenter(
  target: LatLng,
  zoom: number = DEMO_ZOOM,
  offsetPx: number = DEMO_BAND_OFFSET_PX,
): LatLng {
  const metersPerPixel =
    (40_075_016.686 * Math.cos((target.lat * Math.PI) / 180)) / (512 * 2 ** zoom);
  const metersPerDegreeLat = 111_320;
  return { lat: target.lat - (offsetPx * metersPerPixel) / metersPerDegreeLat, lng: target.lng };
}
